import { Component, computed, DestroyRef, effect, inject, Injector, signal, viewChild } from '@angular/core';
import { SalesService } from '../../../services/sales.service';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { LotType, LotTypeLabels, rxResourceFixed } from '@zayan-erp/shared';
import { EMPTY, map, Observable, of, startWith } from 'rxjs';
import { DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CurrencyService } from '@zayan-erp/shared';
import { Popover, PopoverModule } from '@openng/optimus-ui/popover';
import { SalesApsService } from '../../../services/sales-aps.service';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { SpecLotModelResponse } from '@zayan-erp/sales';
import { ElevationService } from '@zayan-erp/home-building';
import { MessageModule } from '@openng/optimus-ui/message';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalService } from '@zayan-erp/shared';
import { LotFormModalComponent } from 'apps/cinari-erp-client/src/app/home-building-module/lot-form-modal/lot-form-modal.component';
import { BuildSpecAndModelDetailsComponent } from '../build-spec-and-model-details/build-spec-and-model-details.component';
@Component({
  selector: 'lib-build-spec-overview',
  imports: [
    GenericDropdownComponent,
    ReactiveFormsModule,
    PopoverModule,
    ConfirmDialogModule,
    MessageModule,
    CommonModule
  ],
  providers: [],
  templateUrl: './build-spec-overview.component.html',
  styleUrl: './build-spec-overview.component.scss'
})
export class BuildSpecOverviewComponent {
  #salesService = inject(SalesService);
  #activatedRoute = inject(ActivatedRoute);
  #currencyService = inject(CurrencyService);
  #salesApsService = inject(SalesApsService);
  #confirmationService = inject(ConfirmationService);
  #destroyRef = inject(DestroyRef);
  #elevationService = inject(ElevationService);
  #modalService = inject(NgbModal);
  #ngbSharedModalService = inject(NgbModalService);
  #injector = inject(Injector);
  #parent = inject(BuildSpecAndModelDetailsComponent);

  LotType = LotType;

  modelElevationForm = new FormGroup({
    modelId: new FormControl<string | null>(null, [Validators.required]),
    elevationId: new FormControl<string | null>(null, [Validators.required]),
    lotTypeId: new FormControl<string | null>(null, [Validators.required])
  });

  selectedSpecLotId = toSignal(
    (this.#activatedRoute.parent?.paramMap.pipe(map(paramMap => paramMap.get('specLotId'))) as Observable<
      string | null
    >) ?? EMPTY,
    { initialValue: null }
  );

  specLot = this.#parent.specLot;

  selectedModelId = toSignal(
    this.modelElevationForm.get('modelId')!.valueChanges.pipe(startWith(this.modelElevationForm.get('modelId')!.value)),
    { initialValue: null }
  );
  selectedElevationId = toSignal(
    this.modelElevationForm
      .get('elevationId')!
      .valueChanges.pipe(startWith(this.modelElevationForm.get('elevationId')!.value)),
    { initialValue: null }
  );
  selectedLotTypeId = toSignal(
    this.modelElevationForm
      .get('lotTypeId')!
      .valueChanges.pipe(startWith(this.modelElevationForm.get('lotTypeId')!.value)),
    { initialValue: null }
  );

  specLotSelectedModelId = computed(() => this.specLot()?.selectedModel?.id ?? null);
  specLotSelectedElevationId = computed(() => this.specLot()?.selectedElevation?.id ?? null);

  readLotTypes = toSignal(
    of(Object.values(LotType).map(lotType => ({ id: lotType, firstLabel: LotTypeLabels[lotType] })) as DropdownData[]),
    { initialValue: [] }
  );

  isLotTypeSpec = computed(() => this.selectedLotTypeId() === LotType.Spec);

  specModelsResource = rxResourceFixed({
    params: () => ({ projectId: this.specLot()?.projectId ?? null }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.projectId) {
        return of([]);
      }
      return this.#salesService.readSpecLotModelElevations(params.projectId);
    }
  });
  modelsResource = rxResourceFixed({
    params: () => ({ specLotId: this.selectedSpecLotId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.specLotId) {
        return of([]);
      }
      return this.#salesService.readModelsBySpecLotDetails(params.specLotId);
    }
  });
  elevationsResource = rxResourceFixed({
    params: () => ({ specLotId: this.selectedSpecLotId(), modelId: this.selectedModelIdForElevations() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.modelId || !params.specLotId) {
        return of([]);
      }
      return this.#elevationService.getLotModelElevations(params.specLotId, params.modelId);
    }
  });
  elevationsDropdownData = computed<DropdownData[]>(() =>
    this.isLotTypeSpec()
      ? ((this.specModelsResource.value() ?? [])
          .find(model => model.modelId === this.selectedModelId())
          ?.elevations.map(elevation => ({
            id: elevation.id,
            firstLabel: elevation.elevationCode,
            secondLabel: elevation.description
          })) ?? [])
      : (this.elevationsResource.value() ?? []).map(elevation => ({
          id: elevation.id,
          firstLabel: elevation.elevationCode,
          secondLabel: elevation.description
        }))
  );

  eligibilityResource = rxResourceFixed<
    { isEligible: boolean; violatedRules: string[] } | null,
    { lotId: string | null; modelId: string | null; elevationId: string | null; isLotTypeReg: boolean }
  >({
    params: () => ({
      lotId: this.specLot()?.id ?? null,
      modelId: this.selectedModelId(),
      elevationId: this.selectedElevationId(),
      isLotTypeReg: this.isLotTypeReg()
    }),
    defaultValue: null as { isEligible: boolean; violatedRules: string[] } | null,
    stream: ({ params }) => {
      if (params.isLotTypeReg || !params.lotId) {
        return of(null);
      }
      return this.#salesApsService.checkRuntimeEligibility({
        lotId: params.lotId,
        modelId: params.modelId ?? '',
        elevationId: params.elevationId ?? ''
      });
    }
  });

  modelsDropdownData = computed<DropdownData[]>(() =>
    this.isLotTypeSpec()
      ? (this.specModelsResource.value() ?? []).map(model => ({
          id: model.modelId,
          firstLabel: model.modelName,
          secondLabel: null
        }))
      : (this.modelsResource.value() ?? []).map((model: SpecLotModelResponse) => ({
          id: model.modelId,
          firstLabel: model.modelName,
          secondLabel: null,
          data: model.id
        }))
  );
  selectedModelIdForElevations = computed(() =>
    this.isLotTypeSpec()
      ? null
      : (this.modelsDropdownData().find(model => model.id === this.selectedModelId())?.data ?? null)
  );

  isLotTypeReg = computed(() => this.selectedLotTypeId() === LotType.Reg);

  combinedEligibilityPopover = viewChild<Popover>('combinedEligibilityPopover');
  eligibilityResult = computed(() => this.eligibilityResource.value());
  combinedEligibilityRulesMessage = computed(() => this.eligibilityResult()?.violatedRules ?? []);

  /** Current lot type label (e.g. 'model', 'reg', 'spec') from form value. */

  /** True when lot type is NOT reg (so model dropdown is enabled and we can show model violations). */

  constructor() {
    this.modelElevationForm.get('lotTypeId')?.valueChanges.subscribe(value => {
      if (value === LotType.Reg) {
        this.modelElevationForm.get('modelId')?.setValue(null);
        this.modelElevationForm.get('modelId')?.disable();
        this.modelElevationForm.get('elevationId')?.setValue(null);
        this.modelElevationForm.get('elevationId')?.disable();
      } else {
        this.modelElevationForm.get('modelId')?.enable();
        this.modelElevationForm.get('elevationId')?.enable();
      }
    });

    this.modelElevationForm.get('modelId')?.valueChanges.subscribe(value => {
      if (value) {
        this.modelElevationForm.get('elevationId')?.setValue(null);
      }
    });

    effect(() => {
      const lot = this.specLot();
      if (!lot) return;
      this.modelElevationForm.patchValue({
        lotTypeId: lot.lotTypeId ?? null,
        modelId: lot.selectedModel?.id ?? null,
        elevationId: lot.selectedElevation?.id ?? null
      });
    });
  }

  violationRules: string[] = [];
  saveChanges(): void {
    this.violationRules = [];
    if (this.modelElevationForm.invalid) {
      this.modelElevationForm.markAllAsTouched();
      return;
    }
    const lotId = this.specLot()?.id ?? '';
    const modelId = this.modelElevationForm.get('modelId')?.value ?? '';
    const elevationId = this.modelElevationForm.get('elevationId')?.value ?? '';
    if (!lotId || !modelId || !elevationId) {
      this.#doSave();
      return;
    }
    this.#salesApsService
      .checkRuntimeEligibility({ lotId, modelId, elevationId })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (eligible: any) => {
          if (eligible?.isEligible) {
            this.#doSave();
          } else {
            this.violationRules = eligible.violatedRules;
            this.#confirmationService.confirm({
              icon: 'confirm-icon',
              header: 'Rule Violations',
              message:
                'This deal is violating the rules. Are you sure you want to continue?' +
                `<ul><li>The selected model/elevation violates the following rule(s): ${eligible.violatedRules.join('<br /><li>')}</li></ul>`,
              acceptLabel: 'Save anyway',
              rejectLabel: 'Cancel',
              accept: () => {
                this.#doSave();
              },
              reject: () => {
                // do nothing
              }
            });
          }
        }
      });
  }

  #doSave(): void {
    this.#salesService
      .updateSpecLot(this.selectedSpecLotId()!, {
        lotId: this.selectedSpecLotId()!,
        modelId: this.modelElevationForm.get('modelId')?.value ?? null,
        elevationId: this.modelElevationForm.get('elevationId')?.value ?? null,
        lotTypeId: this.modelElevationForm.get('lotTypeId')?.value ?? null
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#salesService.triggerSpecLotsRefresh();
        }
      });
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null) return '—';
    const formatted = this.#currencyService.formatCurrency(value);
    return formatted || '—';
  }

  resetPage(): void {
    this.violationRules = [];
    this.modelElevationForm.patchValue({
      lotTypeId: this.specLot()?.lotTypeId ?? null,
      modelId: this.specLotSelectedModelId() ?? null,
      elevationId: this.specLotSelectedElevationId() ?? null
    });
    this.specModelsResource.reload();
    this.modelsResource.reload();
    this.elevationsResource.reload();
    this.eligibilityResource.reload();
  }

  showCombinedEligibilityRules(event: Event): void {
    const rules = this.combinedEligibilityRulesMessage();
    if (!rules.length) return;
    this.combinedEligibilityPopover()?.show(event);
  }

  hideCombinedEligibilityRules(): void {
    this.combinedEligibilityPopover()?.hide();
  }

  openEditLotModal(): void {
    const lotId = this.specLot()?.id;
    if (!lotId) return;

    const modalRef = this.#modalService.open(LotFormModalComponent, {
      ...this.#ngbSharedModalService.centerModal,
      size: 'xl',
      injector: this.#injector
    });
    const instance = modalRef.componentInstance as LotFormModalComponent;
    instance.lotId.set(lotId);
    instance.embedded.set(true);

    const savedSub = instance.saved.subscribe(() => {
      modalRef.close();
      this.#salesService.triggerSpecLotsRefresh();
    });
    const cancelledSub = instance.cancelled.subscribe(() => modalRef.dismiss());

    modalRef.result.finally(() => {
      savedSub.unsubscribe();
      cancelledSub.unsubscribe();
    });
  }
}
